import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, Wallet, AlertCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const Investments = ({ userData }: { userData?: any }) => {
  const [algos, setAlgos] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetch('https://motilalalgo.com/api/profile/market/algos')
      .then(res => res.json())
      .then(data => setAlgos(data))
      .catch(err => console.error(err));
  }, []);

  if (!userData) return null;

  const currentBalance = parseFloat(userData.wallet_balance || 0);

  const handleInvest = (algo: any) => {
    Swal.fire({
      title: `Invest in ${algo.symbol}`,
      html: `
        <div style="text-align: left; margin-top: 10px;">
           <div style="background: #eff6ff; padding: 10px; border-radius: 8px; margin-bottom: 15px; border: 1px solid #bfdbfe;">
             <p style="font-size: 12px; color: #1e40af; font-weight: bold; margin: 0;">Available Balance: ₹${currentBalance.toFixed(2)}</p>
             <p style="font-size: 12px; color: #1e40af; font-weight: bold; margin: 0;">Min Investment: ₹${algo.min_investment}</p>
           </div>
           <label style="font-size: 13px; font-weight: bold; color: #475569;">Enter Amount (₹)</label>
           <input type="number" id="invest-amount" class="swal2-input" style="margin-top: 5px; width: 100%; max-width: 100%; font-weight: bold;" placeholder="e.g. ${algo.min_investment}">
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#2563eb', 
      confirmButtonText: 'Confirm Investment',
      preConfirm: () => {
        const amount = (document.getElementById('invest-amount') as HTMLInputElement).value;
        if (!amount || parseFloat(amount) <= 0) {
          Swal.showValidationMessage('Please enter a valid amount');
        }
        return parseFloat(amount);
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        const amount = result.value;

        // Front-end par bhi check kar lete hain turant
        if (amount > currentBalance) {
          Swal.fire('Insufficient Balance!', 'Please add funds to your wallet to continue.', 'warning');
          return;
        }

        try {
          const res = await fetch(`https://motilalalgo.com/api/profile/${userData.id}/invest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ algo_id: algo.id, amount })
          });
          const data = await res.json();

          if (data.success) {
            Swal.fire('Invested!', data.message, 'success').then(() => {
               // Reload karke data update kar denge
               window.location.href = '/account/portfolio';
            });
          } else {
            Swal.fire('Error', data.error, 'error');
          }
        } catch (error) {
          Swal.fire('Error', 'Server Error', 'error');
        }
      }
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-10">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Market Algos</h1>
          <p className="text-gray-400 mt-1">Invest in premium algorithmic trading setups.</p>
        </div>
        <div className="bg-[#131b2c] border border-gray-800 px-5 py-3 rounded-2xl flex items-center gap-3">
          <Wallet className="text-blue-500" size={24} />
          <div>
            <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">Available Funds</p>
            <p className="text-xl font-bold text-white leading-tight">₹{currentBalance.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {algos.length === 0 ? (
        <div className="bg-[#131b2c] border border-gray-800 rounded-3xl p-12 text-center text-gray-500">
          <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-bold text-white mb-2">No Active Algos Found</h3>
          <p>The market is currently closed or no algos are active right now.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {algos.map((algo) => (
            <div key={algo.id} className="bg-[#131b2c] border border-gray-800 rounded-3xl p-6 shadow-xl hover:border-blue-500/30 transition-all group">
              <div className="flex justify-between items-start mb-6">
                <div className="w-12 h-12 bg-[#0b1221] rounded-2xl flex items-center justify-center border border-gray-800 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <TrendingUp size={24} className={algo.symbol ? "text-blue-500 group-hover:text-white" : ""} />
                </div>
                <span className="bg-green-500/10 text-green-500 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider border border-green-500/20">Active</span>
              </div>
              
              <h3 className="text-2xl font-black text-white mb-1">{algo.symbol}</h3>
              <p className="text-sm text-gray-400 font-medium mb-6">{algo.stock_name}</p>
              
              <div className="bg-[#0b1221] rounded-xl p-4 mb-6 border border-gray-800">
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Min Investment</p>
                <p className="text-lg font-bold text-white">₹{parseFloat(algo.min_investment).toFixed(2)}</p>
              </div>

              <button 
                onClick={() => handleInvest(algo)}
                className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all active:scale-95 shadow-lg shadow-blue-900/20 flex justify-center items-center gap-2"
              >
                <Target size={18} /> Invest Now
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Investments;